import React, { useState, useEffect } from "react";
import api from "../services/api";
import Swal from "sweetalert2";

// ═══════════════════════════════════════════════════════════════════
// ÓRDENES DE COMPRA — Flujo completo: crear → aprobar → recibir
// ═══════════════════════════════════════════════════════════════════

const ESTADOS = {
  borrador:  { label: "Borrador",  color: "#64748b", bg: "#f1f5f9" },
  aprobada:  { label: "Aprobada",  color: "#0369a1", bg: "#e0f2fe" },
  enviada:   { label: "Enviada",   color: "#7c3aed", bg: "#ede9fe" },
  recibida:  { label: "Recibida",  color: "#d97706", bg: "#fef3c7" },
  cerrada:   { label: "Cerrada",   color: "#065f46", bg: "#d1fae5" },
  cancelada: { label: "Cancelada", color: "#991b1b", bg: "#fee2e2" },
};

const Badge = ({ estado }) => {
  const e = ESTADOS[estado] || ESTADOS.borrador;
  return (
    <span style={{
      background: e.bg, color: e.color,
      padding: "3px 10px", borderRadius: "20px",
      fontSize: "12px", fontWeight: "700"
    }}>{e.label}</span>
  );
};

export default function OrdenesCompra() {
  const [vista, setVista]           = useState("lista"); // lista | nueva | detalle
  const [ordenes, setOrdenes]       = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [materiales, setMateriales] = useState([]);
  const [almacenes, setAlmacenes]   = useState([]);
  const [partidas, setPartidas]     = useState([]);
  const [ordenActual, setOrdenActual] = useState(null);
  const [cargando, setCargando]     = useState(false);
  const [msg, setMsg]               = useState(null);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [config, setConfig]               = useState(null);

  // Form nueva OC
  const [form, setForm] = useState({
    proveedor_id: "",
    proveedor_nombre: "",
    guardar_proveedor: false,
    fecha_entrega_estimada: "",
    impuesto: "0",
    _itbisPct: 18,
    notas: "",
  });
  const [lineas, setLineas] = useState([
    { material_id: "", partida_estandar_id: "", tipo: "mercancia", descripcion: "", unidad: "", cantidad_pedida: "", precio_unitario: "" }
  ]);

  // Form recepción
  const [formRecepcion, setFormRecepcion] = useState({
    almacen_id: "",
    notas_recepcion: "",
    detalles_recibidos: [],
    aplica_retencion: false,
    porcentaje_isr: "",
    porcentaje_itbis: "",
  });

  const mostrarMsg = (texto, tipo = "success") => {
    setMsg({ texto, tipo });
    setTimeout(() => setMsg(null), 4000);
  };

  useEffect(() => { cargarTodo(); }, []);

  const cargarTodo = async () => {
    try {
      const [resOrdenes, resProv, resMat, resAlm, resCfg, resPart] = await Promise.all([
        api.get("/ordenes-compra"),
        api.get("/ordenes-compra/proveedores"),
        api.get("/materiales"),
        api.get("/almacenes").catch(() => ({ data: [] })),
        api.get("/configuracion"),
        api.get("/partidas-estandar").catch(() => ({ data: [] })),
      ]);
      setOrdenes(resOrdenes.data);
      setProveedores(resProv.data);
      setMateriales(resMat.data);
      setAlmacenes(resAlm.data);
      setConfig(resCfg.data);
      setPartidas(resPart.data?.data || resPart.data || []);
      // Setear ITBIS automáticamente si está activo
      if (resCfg.data?.pos_itbis_activo) {
        const itbisCalc = (subtotal) => subtotal * (parseFloat(resCfg.data.pos_itbis_porcentaje || 18) / 100);
        setForm(f => ({ ...f, _itbisPct: parseFloat(resCfg.data.pos_itbis_porcentaje || 18) }));
      }
    } catch (e) {
      console.error(e);
    }
  };

  // ─── NUEVA OC ─────────────────────────────────────────────────
  const agregarLinea = () =>
    setLineas([...lineas, { material_id: "", partida_estandar_id: "", tipo: "mercancia", descripcion: "", unidad: "", cantidad_pedida: "", precio_unitario: "" }]);

  const eliminarLinea = (i) => setLineas(lineas.filter((_, idx) => idx !== i));

  const cambiarLinea = (i, campo, valor) => {
    const nuevas = [...lineas];
    nuevas[i][campo] = valor;
    // Si selecciona material → autocompletar y tipo='mercancia'
    if (campo === "material_id") {
      if (valor) {
        const mat = materiales.find((m) => m.id === parseInt(valor));
        if (mat) {
          nuevas[i].descripcion = mat.nombre;
          nuevas[i].unidad = mat.unidad || "";
          nuevas[i].tipo = "mercancia";
        }
      }
    }
    // Si selecciona partida → autocompletar y tipo='servicio' si no hay material
    if (campo === "partida_estandar_id") {
      if (valor) {
        const part = partidas.find((p) => p.id === parseInt(valor));
        if (part) {
          if (!nuevas[i].descripcion) nuevas[i].descripcion = part.nombre;
          if (!nuevas[i].unidad) nuevas[i].unidad = part.unidad || "";
          if (!nuevas[i].precio_unitario) nuevas[i].precio_unitario = part.precio_referencia || "";
          if (!nuevas[i].material_id) nuevas[i].tipo = "servicio";
        }
      }
    }
    setLineas(nuevas);
  };

  const calcularSubtotal = () =>
    lineas.reduce((sum, l) => sum + (parseFloat(l.cantidad_pedida || 0) * parseFloat(l.precio_unitario || 0)), 0);

  const itbisPct     = config?.pos_itbis_activo ? parseFloat(config.pos_itbis_porcentaje || 18) : 0;
  const calcularITBIS = () => config?.pos_itbis_activo ? calcularSubtotal() * (itbisPct / 100) : 0;
  const calcularTotal = () => calcularSubtotal() + calcularITBIS();
  const calcularRetencionRecepcion = () => {
    const subtotal = formRecepcion.detalles_recibidos.reduce((sum, d) => sum + (parseFloat(d.cantidad_recibida || 0) * parseFloat(d.precio_unitario || 0)), 0);
    const itbis = subtotal * (itbisPct / 100);
    const isr = formRecepcion.aplica_retencion ? Math.round(subtotal * (Number(formRecepcion.porcentaje_isr || 0) / 100) * 100) / 100 : 0;
    const itbisRetenido = formRecepcion.aplica_retencion ? Math.round(itbis * (Number(formRecepcion.porcentaje_itbis || 0) / 100) * 100) / 100 : 0;
    const total = Math.round((isr + itbisRetenido) * 100) / 100;
    return { subtotal, itbis, isr, itbisRetenido, total, neto: Math.round((subtotal + itbis - total) * 100) / 100 };
  };

  const guardarOrden = async () => {
    if (lineas.length === 0 || !lineas[0].descripcion)
      return mostrarMsg("Agrega al menos un producto", "error");

    setCargando(true);
    try {
      await api.post("/ordenes-compra", {
        ...form,
        impuesto: calcularITBIS(),
        detalles: lineas.map((l) => ({
          ...l,
          material_id:         l.material_id         ? parseInt(l.material_id)         : null,
          partida_estandar_id: l.partida_estandar_id ? parseInt(l.partida_estandar_id) : null,
          tipo:                l.tipo || "mercancia",
        })),
      });
      mostrarMsg("✅ Orden creada correctamente");
      setVista("lista");
      setForm({ proveedor_id: "", proveedor_nombre: "", guardar_proveedor: false, fecha_entrega_estimada: "", impuesto: "0", notas: "" });
      setLineas([{ material_id: "", partida_estandar_id: "", tipo: "mercancia", descripcion: "", unidad: "", cantidad_pedida: "", precio_unitario: "" }]);
      cargarTodo();
    } catch (e) {
      mostrarMsg("Error al crear orden", "error");
    } finally {
      setCargando(false);
    }
  };

  // ─── APROBAR ──────────────────────────────────────────────────
  const aprobar = async (id) => {
    try {
      await api.patch(`/ordenes-compra/${id}/aprobar`);
      mostrarMsg("✅ Orden aprobada");
      cargarTodo();
      if (ordenActual?.id === id) {
        const res = await api.get(`/ordenes-compra/${id}`);
        setOrdenActual(res.data);
      }
    } catch (e) {
      mostrarMsg(e.response?.data?.error || "Error al aprobar", "error");
    }
  };

  // ─── CANCELAR ─────────────────────────────────────────────────
  const cancelar = async (id) => {
    const { isConfirmed } = await Swal.fire({
      title: '¿Cancelar esta orden?',
      text: '¿Estás seguro de que deseas cancelar esta orden?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, cancelar',
      cancelButtonText: 'No'
    });
    if (!isConfirmed) return;
    try {
      await api.patch(`/ordenes-compra/${id}/cancelar`);
      mostrarMsg("Orden cancelada");
      cargarTodo();
      if (ordenActual?.id === id) setVista("lista");
    } catch (e) {
      mostrarMsg(e.response?.data?.error || "Error al cancelar", "error");
    }
  };

  // ─── VER DETALLE + PREPARAR RECEPCIÓN ─────────────────────────
  const verDetalle = async (id) => {
    const res = await api.get(`/ordenes-compra/${id}`);
    const orden = res.data;
    setOrdenActual(orden);
    setFormRecepcion({
      almacen_id: almacenes[0]?.id || "",
      notas_recepcion: "",
      detalles_recibidos: orden.detalles.map((d) => ({
        detalle_id: d.id,
        descripcion: d.descripcion,
        cantidad_pedida: d.cantidad_pedida,
        cantidad_recibida: parseFloat(d.cantidad_pedida) - parseFloat(d.cantidad_recibida || 0),
        precio_unitario: d.precio_unitario,
        tiene_material: !!d.material_id,
      })),
      aplica_retencion: false,
      porcentaje_isr: "",
      porcentaje_itbis: "",
    });
    setVista("detalle");
  };

  // ─── RECIBIR ──────────────────────────────────────────────────
  const recibirMercancia = async () => {
    if (formRecepcion.aplica_retencion) {
      const rates = [formRecepcion.porcentaje_isr, formRecepcion.porcentaje_itbis].map(Number);
      if (rates.some(rate => !Number.isFinite(rate) || rate < 0 || rate > 100 || Number(rate.toFixed(2)) !== rate)) {
        return mostrarMsg("Los porcentajes deben estar entre 0 y 100 y tener máximo dos decimales", "error");
      }
      if (!rates.some(rate => rate > 0)) return mostrarMsg("Indica al menos un porcentaje mayor que cero", "error");
    }
    setCargando(true);
    try {
      const res = await api.patch(`/ordenes-compra/${ordenActual.id}/recibir`, {
        almacen_id: formRecepcion.almacen_id || null,
        notas_recepcion: formRecepcion.notas_recepcion,
        aplica_retencion: formRecepcion.aplica_retencion,
        porcentaje_isr: formRecepcion.porcentaje_isr === "" ? null : Number(formRecepcion.porcentaje_isr),
        porcentaje_itbis: formRecepcion.porcentaje_itbis === "" ? null : Number(formRecepcion.porcentaje_itbis),
        detalles_recibidos: formRecepcion.detalles_recibidos.map((d) => ({
          detalle_id: d.detalle_id,
          cantidad_recibida: parseFloat(d.cantidad_recibida || 0),
          precio_unitario: parseFloat(d.precio_unitario || 0),
        })),
      });
      mostrarMsg(res.data.mensaje);
      cargarTodo();
      setVista("lista");
    } catch (e) {
      mostrarMsg(e.response?.data?.error || "Error al recibir", "error");
    } finally {
      setCargando(false);
    }
  };


  // ─── GENERAR PDF ──────────────────────────────────────────────
  const generarPDF = (orden) => {
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    script.onload = () => {
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const W = 210; // ancho A4
      const margen = 15;

      // ── ENCABEZADO empresa ──
      doc.setFillColor(30, 64, 175); // azul
      doc.rect(0, 0, W, 28, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(config?.empresa_nombre || "Empresa", margen, 11);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text(`RNC: ${config?.empresa_rnc || ""}`, margen, 17);
      doc.text(`Tel: ${config?.empresa_telefono || ""}  |  ${config?.empresa_email || ""}`, margen, 22);
      doc.text(config?.empresa_direccion || "", margen, 27);

      // ── TÍTULO OC ──
      doc.setTextColor(30, 64, 175);
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("ORDEN DE COMPRA", W - margen, 18, { align: "right" });
      doc.setFontSize(12);
      doc.text(orden.numero, W - margen, 25, { align: "right" });

      let y = 38;

      // ── DATOS PROVEEDOR y OC ──
      // Calcular altura dinámica según datos del proveedor
      let alturaBox = 18;
      if (orden.proveedor?.rnc)      alturaBox += 5;
      if (orden.proveedor?.telefono) alturaBox += 5;
      if (orden.proveedor?.email)    alturaBox += 5;
      if (orden.proveedor?.contacto) alturaBox += 5;
      const alturaFinal = Math.max(alturaBox, 28);

      doc.setDrawColor(226, 232, 240);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(margen, y, 85, alturaFinal, 2, 2, "FD");
      doc.roundedRect(margen + 90, y, 95, alturaFinal, 2, 2, "FD");

      doc.setTextColor(100, 116, 139);
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.text("PROVEEDOR", margen + 3, y + 6);
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(orden.proveedor?.nombre || orden.proveedor_nombre || "—", margen + 3, y + 12);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(71, 85, 105);
      let yProv = y + 18;
      if (orden.proveedor?.rnc)       { doc.text(`RNC: ${orden.proveedor.rnc}`,       margen + 3, yProv); yProv += 5; }
      if (orden.proveedor?.telefono)  { doc.text(`Tel: ${orden.proveedor.telefono}`,   margen + 3, yProv); yProv += 5; }
      if (orden.proveedor?.email)     { doc.text(`Email: ${orden.proveedor.email}`,    margen + 3, yProv); yProv += 5; }
      if (orden.proveedor?.contacto)  { doc.text(`Contacto: ${orden.proveedor.contacto}`, margen + 3, yProv); }

      doc.setTextColor(100, 116, 139);
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.text("DETALLES DE LA ORDEN", margen + 93, y + 6);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(15, 23, 42);
      doc.text(`Número:`,        margen + 93, y + 13); doc.setFont("helvetica","bold"); doc.text(orden.numero, margen + 118, y + 13);
      doc.setFont("helvetica","normal");
      doc.text(`Fecha emisión:`, margen + 93, y + 18); doc.text(orden.fecha_emision || "", margen + 120, y + 18);
      doc.text(`Estado:`,        margen + 93, y + 23); doc.text((orden.estado || "").toUpperCase(), margen + 108, y + 23);
      if (orden.fecha_entrega_estimada) {
        doc.text(`Entrega est.:`, margen + 93, y + 28); doc.text(orden.fecha_entrega_estimada, margen + 118, y + 28);
      }

      y += alturaFinal + 6;

      // ── TABLA PRODUCTOS ──
      const colX    = [margen, margen+8, margen+85, margen+105, margen+120, margen+145, margen+170];
      const headers = ["#", "Descripción", "Unidad", "Cantidad", "Precio Unit.", "Subtotal"];

      // Header tabla
      doc.setFillColor(30, 64, 175);
      doc.rect(margen, y, W - margen * 2, 7, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      headers.forEach((h, i) => doc.text(h, colX[i] + 2, y + 5));
      y += 7;

      // Filas
      const detalles = orden.detalles || [];
      detalles.forEach((d, i) => {
        const bg = i % 2 === 0 ? [255,255,255] : [248,250,252];
        doc.setFillColor(...bg);
        doc.rect(margen, y, W - margen * 2, 7, "F");
        doc.setTextColor(15, 23, 42);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text(String(i + 1),                                          colX[0] + 2, y + 5);
        doc.text((d.descripcion || "").substring(0, 30),                 colX[1] + 2, y + 5);
        doc.text(d.unidad || "—",                                        colX[2] + 2, y + 5);
        doc.text(String(d.cantidad_pedida),                              colX[3] + 2, y + 5);
        doc.text(`RD$ ${parseFloat(d.precio_unitario||0).toFixed(2)}`,   colX[4] + 2, y + 5);
        doc.text(`RD$ ${parseFloat(d.subtotal||0).toFixed(2)}`,          colX[5] + 2, y + 5);
        y += 7;
      });

      // Línea cierre tabla
      doc.setDrawColor(226, 232, 240);
      doc.line(margen, y, W - margen, y);
      y += 6;

      // ── TOTALES ──
      const totalesX = W - margen - 60;
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(71, 85, 105);
      doc.text("Subtotal:",  totalesX, y);
      doc.text(`RD$ ${parseFloat(orden.subtotal||0).toLocaleString("es-DO",{minimumFractionDigits:2})}`, W - margen, y, {align:"right"});
      y += 6;
      doc.text("ITBIS:",     totalesX, y);
      doc.text(`RD$ ${parseFloat(orden.impuesto||0).toLocaleString("es-DO",{minimumFractionDigits:2})}`, W - margen, y, {align:"right"});
      y += 2;
      doc.setDrawColor(30, 64, 175);
      doc.setLineWidth(0.5);
      doc.line(totalesX, y, W - margen, y);
      y += 5;
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 64, 175);
      doc.text("TOTAL:",     totalesX, y);
      doc.text(`RD$ ${parseFloat(orden.total||0).toLocaleString("es-DO",{minimumFractionDigits:2})}`, W - margen, y, {align:"right"});
      doc.setLineWidth(0.2);

      // ── NOTAS ──
      if (orden.notas) {
        y += 12;
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(71, 85, 105);
        doc.text("Notas:", margen, y);
        doc.setFont("helvetica", "normal");
        doc.text(orden.notas, margen + 12, y);
      }

      // ── PIE ──
      y += 20;
      doc.setDrawColor(226, 232, 240);
      doc.line(margen, y, margen + 55, y);
      doc.line(W - margen - 55, y, W - margen, y);
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text("Autorizado por", margen + 10, y + 5);
      doc.text("Recibido por", W - margen - 40, y + 5);

      doc.save(`${orden.numero}.pdf`);
    };
    document.head.appendChild(script);
  };

  const ordenesFiltradas = filtroEstado
    ? ordenes.filter((o) => o.estado === filtroEstado)
    : ordenes;

  // ═══════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════
  return (
    <div style={s.container}>
      {/* MENSAJE */}
      {msg && (
        <div style={{ ...s.msg, background: msg.tipo === "error" ? "#fee2e2" : "#d1fae5", color: msg.tipo === "error" ? "#991b1b" : "#065f46" }}>
          {msg.texto}
        </div>
      )}

      {/* HEADER */}
      <div style={s.header}>
        <div>
          <h1 style={s.titulo}>🛒 Órdenes de Compra</h1>
          <p style={s.subtitulo}>Gestiona compras, aprobaciones y recepciones de mercancía</p>
        </div>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {vista !== "lista" && (
            <button style={s.btnSecondary} onClick={() => setVista("lista")}>← Volver</button>
          )}
          {vista === "lista" && (
            <>
              <button style={{ ...s.btnSecondary, background: '#7c3aed', color: '#fff' }} onClick={() => window.location.href = '/cuentas-pagar'}>
                💳 Cuentas por Pagar
              </button>
              <button style={s.btnPrimary} onClick={() => setVista("nueva")}>➕ Nueva Orden</button>
            </>
          )}
        </div>
      </div>

      {/* ── LISTA ───────────────────────────────────────────────── */}
      {vista === "lista" && (
        <>
          {/* Filtros */}
          <div style={s.filtros}>
            {["", "borrador", "aprobada", "enviada", "recibida", "cerrada", "cancelada"].map((e) => (
              <button key={e} onClick={() => setFiltroEstado(e)}
                style={{ ...s.filtroBtn, ...(filtroEstado === e ? s.filtroBtnActivo : {}) }}>
                {e === "" ? "Todas" : ESTADOS[e]?.label}
              </button>
            ))}
          </div>

          {/* Estadísticas rápidas */}
          <div style={s.statsRow}>
            {["borrador", "aprobada", "recibida", "cerrada"].map((e) => (
              <div key={e} style={{ ...s.statCard, borderLeft: `4px solid ${ESTADOS[e].color}` }}>
                <div style={{ fontSize: "22px", fontWeight: "800", color: ESTADOS[e].color }}>
                  {ordenes.filter((o) => o.estado === e).length}
                </div>
                <div style={{ fontSize: "12px", color: "#64748b" }}>{ESTADOS[e].label}</div>
              </div>
            ))}
          </div>

          {/* Tabla */}
          <div style={s.card}>
            {ordenesFiltradas.length === 0 ? (
              <div style={s.vacio}>📋 No hay órdenes de compra{filtroEstado ? ` en estado "${ESTADOS[filtroEstado]?.label}"` : ""}</div>
            ) : (
              <table style={s.tabla}>
                <thead>
                  <tr>
                    {["#", "Número", "Proveedor", "Fecha", "Total", "Estado", "Acciones"].map((h) => (
                      <th key={h} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ordenesFiltradas.map((o, i) => (
                    <tr key={o.id} style={s.tr}>
                      <td style={s.td}>{i + 1}</td>
                      <td style={{ ...s.td, fontWeight: "700", color: "#1e40af" }}>{o.numero}</td>
                      <td style={s.td}>{o.proveedor?.nombre || o.proveedor_nombre || "—"}</td>
                      <td style={s.td}>{o.fecha_emision}</td>
                      <td style={{ ...s.td, fontWeight: "700" }}>RD$ {parseFloat(o.total).toLocaleString("es-DO", { minimumFractionDigits: 2 })}</td>
                      <td style={s.td}><Badge estado={o.estado} /></td>
                      <td style={s.td}>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button style={s.btnIcon} onClick={() => verDetalle(o.id)} title="Ver detalle">👁️</button>
                          <button style={{ ...s.btnIcon, background: "#eff6ff" }} onClick={() => generarPDF(o)} title="Imprimir PDF">🖨️</button>
                          {o.estado === "borrador" && (
                            <button style={{ ...s.btnIcon, background: "#dbeafe" }} onClick={() => aprobar(o.id)} title="Aprobar">✅</button>
                          )}
                          {["aprobada", "enviada", "recibida"].includes(o.estado) && (
                            <button style={{ ...s.btnIcon, background: "#d1fae5" }} onClick={() => verDetalle(o.id)} title="Recibir mercancía">📦</button>
                          )}
                          {!["cerrada", "cancelada"].includes(o.estado) && (
                            <button style={{ ...s.btnIcon, background: "#fee2e2" }} onClick={() => cancelar(o.id)} title="Cancelar">❌</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ── NUEVA OC ────────────────────────────────────────────── */}
      {vista === "nueva" && (
        <div style={s.card}>
          <h2 style={s.cardTitulo}>📝 Nueva Orden de Compra</h2>

          {/* Proveedor */}
          <div style={s.seccion}>
            <h3 style={s.seccionTitulo}>Proveedor</h3>
            <div style={s.grid2}>
              <div style={s.formGroup}>
                <label style={s.label}>Proveedor registrado</label>
                <select style={s.input} value={form.proveedor_id}
                  onChange={(e) => setForm({ ...form, proveedor_id: e.target.value, proveedor_nombre: "" })}>
                  <option value="">— Seleccionar o escribir nuevo —</option>
                  {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select>
              </div>
              {!form.proveedor_id && (
                <div style={s.formGroup}>
                  <label style={s.label}>O escribir nombre del proveedor</label>
                  <input style={s.input} placeholder="Nombre del proveedor"
                    value={form.proveedor_nombre}
                    onChange={(e) => setForm({ ...form, proveedor_nombre: e.target.value })} />
                  {form.proveedor_nombre && (
                    <label style={{ fontSize: "12px", color: "#64748b", display: "flex", alignItems: "center", gap: "6px", marginTop: "6px" }}>
                      <input type="checkbox" checked={form.guardar_proveedor}
                        onChange={(e) => setForm({ ...form, guardar_proveedor: e.target.checked })} />
                      Guardar en el catálogo de proveedores
                    </label>
                  )}
                </div>
              )}
              <div style={s.formGroup}>
                <label style={s.label}>Fecha de entrega estimada</label>
                <input type="date" style={s.input} value={form.fecha_entrega_estimada}
                  onChange={(e) => setForm({ ...form, fecha_entrega_estimada: e.target.value })} />
              </div>
            </div>
          </div>

          {/* Productos */}
          <div style={s.seccion}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <h3 style={s.seccionTitulo}>Productos</h3>
              <button style={s.btnSecondary} onClick={agregarLinea}>+ Agregar línea</button>
            </div>

            <div style={{ width: "100%" }}>
              {(() => {
                const tieneInventario = config?.modulo_oficina ?? false;
                const tieneProyectos  = config?.modulo_proyectos ?? false;
                return (
                  <table style={s.tabla}>
                    <thead>
                      <tr>
                        <th style={s.th}>Tipo</th>
                        {tieneInventario && <th style={s.th}>Material (opcional)</th>}
                        {tieneProyectos  && <th style={s.th}>Partida Estándar (opcional)</th>}
                        <th style={s.th}>{tieneInventario ? "Descripción *" : "Descripción del artículo *"}</th>
                        <th style={s.th}>Unidad</th>
                        <th style={s.th}>Cantidad *</th>
                        <th style={s.th}>Precio Unit.</th>
                        <th style={s.th}>Subtotal</th>
                        <th style={s.th}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineas.map((l, i) => (
                        <tr key={i}>
                          <td style={s.td}>
                            <select style={{ ...s.input, fontSize: "13px", minWidth: "130px" }}
                              value={l.tipo || "mercancia"}
                              onChange={(e) => cambiarLinea(i, "tipo", e.target.value)}>
                              <option value="mercancia">📦 Mercancía</option>
                              <option value="servicio">🔧 Servicio</option>
                              <option value="gasto">💰 Gasto</option>
                              <option value="activo_fijo">🏗️ Activo Fijo</option>
                            </select>
                          </td>
                          {tieneInventario && (
                            <td style={s.td}>
                              <select style={{ ...s.input, minWidth: "140px", fontSize: "13px" }} value={l.material_id}
                                onChange={(e) => cambiarLinea(i, "material_id", e.target.value)}>
                                <option value="">— Ninguno —</option>
                                {materiales.map((m) => <option key={m.id} value={m.id}>{m.codigo} — {m.nombre}</option>)}
                              </select>
                            </td>
                          )}
                          {tieneProyectos && (
                            <td style={s.td}>
                              <select style={{ ...s.input, minWidth: "150px", fontSize: "13px" }} value={l.partida_estandar_id}
                                onChange={(e) => cambiarLinea(i, "partida_estandar_id", e.target.value)}>
                                <option value="">— Ninguna —</option>
                                {partidas.map((p) => <option key={p.id} value={p.id}>[{p.codigo}] {p.nombre}</option>)}
                              </select>
                            </td>
                          )}
                          <td style={s.td}>
                            <input style={{ ...s.input, minWidth: "130px", fontSize: "13px" }}
                              placeholder={tieneInventario ? "Descripción" : "Ej: Resmas de papel, servicio de limpieza..."}
                              value={l.descripcion} onChange={(e) => cambiarLinea(i, "descripcion", e.target.value)} />
                          </td>
                          <td style={s.td}>
                            <input style={{ ...s.input, width: "60px", fontSize: "13px" }} placeholder="Und"
                              value={l.unidad} onChange={(e) => cambiarLinea(i, "unidad", e.target.value)} />
                          </td>
                          <td style={s.td}>
                            <input type="number" style={{ ...s.input, width: "75px", fontSize: "13px" }} placeholder="0"
                              value={l.cantidad_pedida} onChange={(e) => cambiarLinea(i, "cantidad_pedida", e.target.value)} />
                          </td>
                          <td style={s.td}>
                            <input type="number" style={{ ...s.input, width: "90px", fontSize: "13px" }} placeholder="0.00"
                              value={l.precio_unitario} onChange={(e) => cambiarLinea(i, "precio_unitario", e.target.value)} />
                          </td>
                          <td style={{ ...s.td, fontWeight: "700" }}>
                            RD$ {(parseFloat(l.cantidad_pedida || 0) * parseFloat(l.precio_unitario || 0)).toLocaleString("es-DO", { minimumFractionDigits: 2 })}
                          </td>
                          <td style={s.td}>
                            {lineas.length > 1 && (
                              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: "16px" }}
                                onClick={() => eliminarLinea(i)}>🗑️</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>

          {/* Totales y notas */}
          <div style={{ display: "flex", justifyContent: "space-between", gap: "24px", flexWrap: "wrap" }}>
            <div style={{ ...s.formGroup, flex: 1, minWidth: "200px" }}>
              <label style={s.label}>Notas</label>
              <textarea style={{ ...s.input, height: "80px", resize: "vertical" }}
                placeholder="Observaciones, instrucciones de entrega..."
                value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
            </div>
            <div style={{ background: "#f8fafc", borderRadius: "12px", padding: "20px", minWidth: "220px" }}>
              <div style={s.totalRow}><span>Subtotal:</span><span>RD$ {calcularSubtotal().toLocaleString("es-DO", { minimumFractionDigits: 2 })}</span></div>
              <div style={s.totalRow}>
                <span>ITBIS {config?.pos_itbis_activo ? `(${itbisPct}%)` : "(inactivo)"}:</span>
                <span style={{ fontWeight: "600", color: config?.pos_itbis_activo ? "#1e40af" : "#94a3b8" }}>
                  RD$ {calcularITBIS().toLocaleString("es-DO", { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div style={{ ...s.totalRow, fontWeight: "800", fontSize: "18px", borderTop: "2px solid #e2e8f0", paddingTop: "10px", marginTop: "10px" }}>
                <span>Total:</span><span style={{ color: "#1e40af" }}>RD$ {calcularTotal().toLocaleString("es-DO", { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
            <button style={s.btnSecondary} onClick={() => setVista("lista")}>Cancelar</button>
            <button style={s.btnPrimary} onClick={guardarOrden} disabled={cargando}>
              {cargando ? "Guardando..." : "💾 Guardar Orden"}
            </button>
          </div>
        </div>
      )}

      {/* ── DETALLE / RECEPCIÓN ─────────────────────────────────── */}
      {vista === "detalle" && ordenActual && (
        <div style={s.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
            <div>
              <h2 style={s.cardTitulo}>{ordenActual.numero} <Badge estado={ordenActual.estado} /></h2>
              <p style={{ color: "#64748b", margin: "4px 0 0" }}>
                Proveedor: <strong>{ordenActual.proveedor?.nombre || ordenActual.proveedor_nombre || "—"}</strong>
                {ordenActual.fecha_entrega_estimada && ` · Entrega estimada: ${ordenActual.fecha_entrega_estimada}`}
              </p>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              {ordenActual.estado === "borrador" && (
                <button style={s.btnPrimary} onClick={() => aprobar(ordenActual.id)}>✅ Aprobar</button>
              )}
              <button style={{ ...s.btnSecondary, background: "#eff6ff", color: "#1e40af" }} onClick={() => generarPDF(ordenActual)}>🖨️ Imprimir PDF</button>
              {!["cerrada", "cancelada"].includes(ordenActual.estado) && (
                <button style={{ ...s.btnSecondary, color: "#ef4444" }} onClick={() => cancelar(ordenActual.id)}>❌ Cancelar</button>
              )}
            </div>
          </div>

          {/* Tabla de detalles */}
          <div style={{ overflowX: "auto", marginBottom: "24px" }}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}>Descripción</th>
                  <th style={s.th}>Unidad</th>
                  <th style={s.th}>Pedido</th>
                  <th style={s.th}>Recibido</th>
                  <th style={s.th}>Precio Unit.</th>
                  <th style={s.th}>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {ordenActual.detalles?.map((d) => (
                  <tr key={d.id} style={s.tr}>
                    <td style={s.td}>
                      <div style={{ fontWeight: "600" }}>{d.descripcion}</div>
                      <div style={{ fontSize: "11px", color: "#64748b", display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "2px" }}>
                        {d.tipo === "mercancia"   && <span>📦 Mercancía</span>}
                        {d.tipo === "servicio"    && <span>🔧 Servicio</span>}
                        {d.tipo === "gasto"       && <span>💰 Gasto</span>}
                        {d.tipo === "activo_fijo" && <span>🏗️ Activo Fijo</span>}
                        {d.material && <span>· {d.material.codigo}</span>}
                        {d.partida  && <span style={{ color: "#7c3aed" }}>· {d.partida.codigo} {d.partida.nombre}</span>}
                      </div>
                    </td>
                    <td style={s.td}>{d.unidad || "—"}</td>
                    <td style={s.td}>{d.cantidad_pedida}</td>
                    <td style={s.td}>
                      <span style={{ color: parseFloat(d.cantidad_recibida) >= parseFloat(d.cantidad_pedida) ? "#065f46" : "#92400e", fontWeight: "700" }}>
                        {d.cantidad_recibida || 0}
                      </span>
                    </td>
                    <td style={s.td}>RD$ {parseFloat(d.precio_unitario).toLocaleString("es-DO", { minimumFractionDigits: 2 })}</td>
                    <td style={{ ...s.td, fontWeight: "700" }}>RD$ {parseFloat(d.subtotal).toLocaleString("es-DO", { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totales */}
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "24px" }}>
            <div style={{ background: "#f8fafc", borderRadius: "12px", padding: "16px", minWidth: "220px" }}>
              <div style={s.totalRow}><span>Subtotal:</span><span>RD$ {parseFloat(ordenActual.subtotal).toLocaleString("es-DO", { minimumFractionDigits: 2 })}</span></div>
              <div style={s.totalRow}><span>ITBIS:</span><span>RD$ {parseFloat(ordenActual.impuesto).toLocaleString("es-DO", { minimumFractionDigits: 2 })}</span></div>
              <div style={{ ...s.totalRow, fontWeight: "800", fontSize: "18px", borderTop: "2px solid #e2e8f0", paddingTop: "10px", marginTop: "10px" }}>
                <span>Total:</span><span style={{ color: "#1e40af" }}>RD$ {parseFloat(ordenActual.total).toLocaleString("es-DO", { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>

          {/* RECEPCIÓN — solo si está aprobada o enviada */}
          {["aprobada", "enviada", "recibida"].includes(ordenActual.estado) && (() => {
            const tieneInventario = config?.modulo_oficina ?? false;
            return (
              <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: "12px", padding: "20px" }}>
                <h3 style={{ margin: "0 0 16px", color: "#065f46" }}>
                  {tieneInventario ? "📦 Recibir Mercancía" : "✅ Registrar Recepción"}
                </h3>

              <div style={{ display: "flex", gap: "16px", marginBottom: "16px", flexWrap: "wrap" }}>
                  {tieneInventario && (
                    <div style={{ ...s.formGroup, flex: 1, minWidth: "200px" }}>
                      <label style={s.label}>Almacén de destino</label>
                      <select style={s.input} value={formRecepcion.almacen_id}
                        onChange={(e) => setFormRecepcion({ ...formRecepcion, almacen_id: e.target.value })}>
                        <option value="">— Sin asignar almacén —</option>
                        {almacenes.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                      </select>
                    </div>
                  )}
                  <div style={{ ...s.formGroup, flex: 1, minWidth: "200px" }}>
                    <label style={s.label}>Notas de recepción</label>
                    <input style={s.input} placeholder="Observaciones..."
                      value={formRecepcion.notas_recepcion}
                      onChange={(e) => setFormRecepcion({ ...formRecepcion, notas_recepcion: e.target.value })} />
                  </div>
              </div>

              <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "8px", padding: "14px", marginBottom: "16px" }}>
                <label style={s.label}>¿Esta factura lleva retención?</label>
                <select style={s.input} value={formRecepcion.aplica_retencion ? "si" : "no"}
                  onChange={(e) => setFormRecepcion({ ...formRecepcion, aplica_retencion: e.target.value === "si", ...(e.target.value === "no" ? { porcentaje_isr: "", porcentaje_itbis: "" } : {}) })}>
                  <option value="no">No</option>
                  <option value="si">Sí</option>
                </select>
                {formRecepcion.aplica_retencion && (
                  <div style={{ display: "flex", gap: "16px", marginTop: "12px", flexWrap: "wrap" }}>
                    <div style={{ ...s.formGroup, minWidth: "180px" }}><label style={s.label}>Retención ISR (%)</label><input type="number" min="0" max="100" step="0.01" style={s.input} value={formRecepcion.porcentaje_isr} onChange={(e) => setFormRecepcion({ ...formRecepcion, porcentaje_isr: e.target.value })} placeholder="0.00" /></div>
                    <div style={{ ...s.formGroup, minWidth: "180px" }}><label style={s.label}>Retención ITBIS (%)</label><input type="number" min="0" max="100" step="0.01" style={s.input} value={formRecepcion.porcentaje_itbis} onChange={(e) => setFormRecepcion({ ...formRecepcion, porcentaje_itbis: e.target.value })} placeholder="0.00" /></div>
                  </div>
                )}
                {(calcularRetencionRecepcion().subtotal > 0) && (() => { const r = calcularRetencionRecepcion(); return <div style={{ marginTop: "12px", display: "flex", gap: "16px", flexWrap: "wrap", fontSize: "13px" }}><span>Bruto: <strong>RD$ {(r.subtotal + r.itbis).toFixed(2)}</strong></span><span>ISR retenido: <strong>RD$ {r.isr.toFixed(2)}</strong></span><span>ITBIS retenido: <strong>RD$ {r.itbisRetenido.toFixed(2)}</strong></span><span>Total retenido: <strong>RD$ {r.total.toFixed(2)}</strong></span><span>Neto a pagar: <strong>RD$ {r.neto.toFixed(2)}</strong></span></div>; })()}
              </div>

              <table style={s.tabla}>
                  <thead>
                    <tr>
                      <th style={s.th}>Producto</th>
                      <th style={s.th}>Pedido</th>
                      <th style={s.th}>Ya recibido</th>
                      <th style={s.th}>Cantidad a recibir ahora</th>
                      <th style={s.th}>Precio real</th>
                      <th style={s.th}>{tieneInventario ? "Entrada automática" : "Registro contable"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formRecepcion.detalles_recibidos.map((d, i) => {
                      const det = ordenActual.detalles.find((od) => od.id === d.detalle_id);
                      const yaRecibido = parseFloat(det?.cantidad_recibida || 0);
                      const pendiente = parseFloat(det?.cantidad_pedida || 0) - yaRecibido;
                      return (
                        <tr key={d.detalle_id} style={s.tr}>
                          <td style={s.td}><strong>{d.descripcion}</strong></td>
                          <td style={s.td}>{det?.cantidad_pedida}</td>
                          <td style={s.td}>{yaRecibido}</td>
                          <td style={s.td}>
                            <input type="number" max={pendiente} min="0"
                              style={{ ...s.input, width: "90px" }}
                              value={d.cantidad_recibida}
                              onChange={(e) => {
                                const nuevos = [...formRecepcion.detalles_recibidos];
                                nuevos[i].cantidad_recibida = e.target.value;
                                setFormRecepcion({ ...formRecepcion, detalles_recibidos: nuevos });
                              }} />
                            <span style={{ fontSize: "11px", color: "#64748b", marginLeft: "6px" }}>/ {pendiente} pendiente</span>
                          </td>
                          <td style={s.td}>
                            <input type="number" style={{ ...s.input, width: "100px" }}
                              value={d.precio_unitario}
                              onChange={(e) => {
                                const nuevos = [...formRecepcion.detalles_recibidos];
                                nuevos[i].precio_unitario = e.target.value;
                                setFormRecepcion({ ...formRecepcion, detalles_recibidos: nuevos });
                              }} />
                          </td>
                          <td style={s.td}>
                            {tieneInventario ? (() => {
                              const tipo = det?.tipo || "mercancia";
                              if (tipo === "mercancia" && d.tiene_material)
                                return <span style={{ color: "#065f46", fontWeight: "700" }}>📦 Actualiza stock</span>;
                              if (tipo === "mercancia")
                                return <span style={{ color: "#92400e" }}>📦 Mercancía sin material</span>;
                              if (tipo === "servicio")
                                return <span style={{ color: "#7c3aed" }}>🔧 Servicio — sin stock</span>;
                              if (tipo === "gasto")
                                return <span style={{ color: "#b45309" }}>💰 Gasto — sin stock</span>;
                              if (tipo === "activo_fijo")
                                return <span style={{ color: "#0369a1" }}>🏗️ Activo Fijo — sin stock</span>;
                              return <span style={{ color: "#64748b" }}>—</span>;
                            })() : (
                              <span style={{ color: "#065f46", fontWeight: "700" }}>✅ Genera asiento contable</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
                  <button style={{ ...s.btnPrimary, background: "#059669" }} onClick={recibirMercancia} disabled={cargando}>
                    {cargando ? "Procesando..." : tieneInventario ? "📦 Confirmar Recepción y Generar Entradas" : "✅ Confirmar Recepción"}
                  </button>
                </div>
              </div>
            );
          })()}

          {ordenActual.notas && (
            <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: "8px", padding: "12px", marginTop: "16px" }}>
              <strong>📝 Notas:</strong> {ordenActual.notas}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ESTILOS
// ═══════════════════════════════════════════════════════════════════
const s = {
  container:    { padding: "24px", maxWidth: "100%", margin: "0 auto", fontFamily: "'Segoe UI', sans-serif" },
  header:       { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px", flexWrap: "wrap", gap: "12px" },
  titulo:       { margin: 0, fontSize: "26px", fontWeight: "800", color: "#0f172a" },
  subtitulo:    { margin: "4px 0 0", color: "#64748b", fontSize: "14px" },
  msg:          { padding: "12px 16px", borderRadius: "8px", marginBottom: "16px", fontWeight: "600", fontSize: "14px" },
  card:         { background: "white", borderRadius: "16px", padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,.1)", marginBottom: "20px" },
  cardTitulo:   { margin: "0 0 20px", fontSize: "18px", fontWeight: "700", color: "#0f172a" },
  seccion:      { marginBottom: "24px", paddingBottom: "24px", borderBottom: "1px solid #f1f5f9" },
  seccionTitulo:{ margin: "0 0 12px", fontSize: "15px", fontWeight: "700", color: "#334155" },
  grid2:        { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px,1fr))", gap: "16px" },
  formGroup:    { display: "flex", flexDirection: "column", gap: "6px" },
  label:        { fontSize: "13px", fontWeight: "600", color: "#374151" },
  input:        { padding: "9px 12px", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "14px", outline: "none", background: "white" },
  tabla:        { width: "100%", borderCollapse: "collapse" },
  th:           { padding: "10px 10px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", background: "#f8fafc", borderBottom: "2px solid #e2e8f0" },
  td:           { padding: "10px 10px", fontSize: "13px", color: "#1e293b", borderBottom: "1px solid #f1f5f9" },
  tr:           { transition: "background .15s" },
  btnPrimary:   { padding: "10px 20px", background: "#1e40af", color: "white", border: "none", borderRadius: "8px", fontWeight: "700", cursor: "pointer", fontSize: "14px" },
  btnSecondary: { padding: "10px 20px", background: "#f1f5f9", color: "#334155", border: "none", borderRadius: "8px", fontWeight: "700", cursor: "pointer", fontSize: "14px" },
  btnIcon:      { padding: "6px 10px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "6px", cursor: "pointer", fontSize: "14px" },
  filtros:      { display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" },
  filtroBtn:    { padding: "6px 14px", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "20px", cursor: "pointer", fontSize: "13px", fontWeight: "600", color: "#64748b" },
  filtroBtnActivo: { background: "#1e40af", color: "white", borderColor: "#1e40af" },
  statsRow:     { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "12px", marginBottom: "16px" },
  statCard:     { background: "white", borderRadius: "12px", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,.08)" },
  totalRow:     { display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: "14px" },
  vacio:        { textAlign: "center", padding: "40px", color: "#94a3b8", fontSize: "15px" },
};
